import { useEffect, useMemo, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Send, Megaphone, Download, Trash2, Users } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { format } from "date-fns";

const LIMIT_STEPS = [10, 25, 50, 100, 250, 500, 1000];

export default function Campaigns() {
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ event_id: "", template_id: "", audience: "all", limit: "" });
  const [preview, setPreview] = useState(null); // { audience_size, skipped_already_sent } | null while loading
  const [notSentFor, setNotSentFor] = useState(null); // campaign object | null

  async function loadAll() {
    const [{ data: e }, { data: t }, { data: c }] = await Promise.all([
      api.get("/events"),
      api.get("/templates"),
      api.get("/campaigns"),
    ]);
    setEvents(e);
    setTemplates(t.filter((x) => x.approval_status === "approved"));
    setCampaigns(c);
  }
  useEffect(() => {
    loadAll();
    const iv = setInterval(loadAll, 4000);
    return () => clearInterval(iv);
  }, []);

  // Live count of who'd actually get messaged, so the "limit" dropdown can be
  // capped at a real number instead of the event's total lead count.
  useEffect(() => {
    if (!form.event_id || !form.template_id) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreview(null);
    api
      .get("/campaigns/audience-preview", {
        params: { event_id: form.event_id, template_id: form.template_id, audience: form.audience },
      })
      .then(({ data }) => {
        if (!cancelled) setPreview(data);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [form.event_id, form.template_id, form.audience]);

  useEffect(() => {
    if (preview && form.limit && Number(form.limit) > preview.audience_size) {
      setForm((f) => ({ ...f, limit: "" }));
    }
  }, [preview, form.limit]);

  async function submit(e) {
    e.preventDefault();
    if (!form.event_id || !form.template_id)
      return toast.error("Choose a programme and template");
    try {
      const payload = { ...form, limit: form.limit ? Number(form.limit) : null };
      const { data } = await api.post("/campaigns", payload);
      toast.success(
        data.skipped_already_sent
          ? `Campaign queued for ${data.audience_size} lead(s) — skipped ` +
            `${data.skipped_already_sent} who already got this template`
          : `Campaign queued for ${data.audience_size} lead(s)`,
      );
      setOpen(false);
      setForm({ event_id: "", template_id: "", audience: "all", limit: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function downloadCampaign(id) {
    const token = localStorage.getItem("wa_crm_token");
    const res = await fetch(`${API_BASE}/reports/campaign/${id}.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `campaign_${id}.csv`;
    a.click();
  }

  async function deleteCampaign(id) {
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaign deleted");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  async function openNotSent(campaign) {
    try {
      const { data } = await api.get(`/campaigns/${campaign.id}`);
      setNotSentFor(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  const rows = useMemo(() => campaigns, [campaigns]);

  return (
    <div className="mx-auto max-w-7xl p-6 md:p-10" data-testid="campaigns-page">
      <PageHeader
        overline="Broadcast"
        title="Campaigns"
        subtitle="Send approved templates in bulk. Track delivery, reads and replies per lead."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-campaign-btn">
                <Send className="mr-2 h-4 w-4" /> New campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Send a new campaign</DialogTitle>
                <DialogDescription>
                  Pick a programme and an approved template. Messages are queued and delivered in the background.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="new-campaign-form">
                <div className="space-y-2">
                  <Label>Programme</Label>
                  <Select
                    value={form.event_id}
                    onValueChange={(v) => setForm({ ...form, event_id: v })}
                  >
                    <SelectTrigger data-testid="campaign-event-select">
                      <SelectValue placeholder="Choose programme" />
                    </SelectTrigger>
                    <SelectContent>
                      {events.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} ({e.stats?.total ?? 0} leads)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Approved template</Label>
                  <Select
                    value={form.template_id}
                    onValueChange={(v) => setForm({ ...form, template_id: v })}
                  >
                    <SelectTrigger data-testid="campaign-template-select">
                      <SelectValue placeholder="Choose template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name} · {t.kind}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {templates.length === 0 && (
                    <p className="text-xs text-muted-foreground">
                      No approved templates. Approve one from the Templates page.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Send to</Label>
                  <RadioGroup
                    value={form.audience}
                    onValueChange={(v) => setForm({ ...form, audience: v })}
                    className="gap-2"
                    data-testid="campaign-audience-radio"
                  >
                    <label className="flex items-center gap-2 text-sm font-normal">
                      <RadioGroupItem value="all" data-testid="campaign-audience-all" />
                      All leads on this programme
                    </label>
                    <label className="flex items-center gap-2 text-sm font-normal">
                      <RadioGroupItem value="unsent" data-testid="campaign-audience-unsent" />
                      Only those who haven't received this template yet
                    </label>
                  </RadioGroup>
                  <p className="text-xs text-muted-foreground">
                    Avoids messaging the same person twice if you run this template again.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Limit to (optional)</Label>
                  <Select
                    value={form.limit ? String(form.limit) : "__all__"}
                    onValueChange={(v) => setForm({ ...form, limit: v === "__all__" ? "" : v })}
                    disabled={!preview || preview.audience_size === 0}
                  >
                    <SelectTrigger data-testid="campaign-limit-select">
                      <SelectValue
                        placeholder={
                          form.event_id && form.template_id ? "Loading eligible leads…" : "Choose programme & template first"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">
                        Send to all {preview ? `(${preview.audience_size})` : ""} eligible leads
                      </SelectItem>
                      {preview &&
                        LIMIT_STEPS.filter((n) => n < preview.audience_size).map((n) => (
                          <SelectItem key={n} value={String(n)}>
                            First {n} leads
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Send to a smaller batch first to protect your WhatsApp quality rating, instead of
                    messaging everyone at once.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="campaign-submit-btn">
                    Queue &amp; send
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Card className="mt-8 border-border shadow-sm" data-testid="campaigns-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="px-6 text-xs uppercase tracking-widest">Template</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Programme</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Delivery</TableHead>
              <TableHead className="text-xs uppercase tracking-widest">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="p-12 text-center text-sm text-muted-foreground">
                  <Megaphone className="mx-auto mb-2 h-8 w-8 text-fun-magenta" />
                  No campaigns yet. Send your first invite.
                </TableCell>
              </TableRow>
            )}
            {rows.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="px-6 py-4 font-semibold">{c.template_name}</TableCell>
                <TableCell className="text-sm">{c.event_name}</TableCell>
                <TableCell>
                  <Badge
                    className={`rounded-full ${
                      c.status === "completed"
                        ? "bg-fun-green-tint text-fun-green"
                        : "bg-fun-yellow-tint text-fun-yellow"
                    }`}
                  >
                    {c.status}
                  </Badge>
                </TableCell>
                <TableCell className="py-4">
                  <div className="flex items-center gap-3 text-xs">
                    <Stat label="Sent" value={c.sent} tone="text-fun-blue" />
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-widest text-muted-foreground">
                    Audience: {c.audience_size} · {safeDate(c.created_at)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadCampaign(c.id)}
                      data-testid={`campaign-download-${c.id}`}
                    >
                      <Download className="mr-1.5 h-3.5 w-3.5" /> CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openNotSent(c)}
                      data-testid={`campaign-not-sent-${c.id}`}
                    >
                      <Users className="mr-1.5 h-3.5 w-3.5" /> Not sent
                    </Button>
                    <ConfirmDialog
                      testId={`campaign-delete-dialog-${c.id}`}
                      title={`Delete this campaign?`}
                      description={
                        `This removes the "${c.template_name}" campaign for ${c.event_name} and ` +
                        "its recipient records. Messages already sent stay in the conversation " +
                        "history. This cannot be undone."
                      }
                      onConfirm={() => deleteCampaign(c.id)}
                      trigger={
                        <Button
                          variant="ghost"
                          size="sm"
                          title="Delete campaign"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          data-testid={`campaign-delete-${c.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      }
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!notSentFor} onOpenChange={(o) => !o && setNotSentFor(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="not-sent-dialog">
          <DialogHeader>
            <DialogTitle>Leads not messaged</DialogTitle>
            <DialogDescription>
              {notSentFor && (
                <>
                  &ldquo;{notSentFor.template_name}&rdquo; for {notSentFor.event_name}
                  {notSentFor.limit_applied ? (
                    <> — this run was limited to {notSentFor.limit_applied} of {" "}
                      {notSentFor.limit_applied + notSentFor.skipped_by_limit} eligible leads.</>
                  ) : null}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-1 overflow-y-auto">
            {notSentFor &&
              (() => {
                const notSent = notSentFor.recipients.filter((r) => r.status !== "sent" && r.status !== "read" && r.status !== "delivered" && r.status !== "replied");
                if (notSent.length === 0)
                  return <p className="p-4 text-center text-sm text-muted-foreground">Everyone in this campaign was messaged.</p>;
                return notSent.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <div>
                      <div className="font-medium">{r.lead?.name || r.phone}</div>
                      <div className="text-xs text-muted-foreground">{r.phone}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {r.status === "failed" ? r.error || "failed" : r.status}
                    </Badge>
                  </div>
                ));
              })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, tone = "" }) {
  return (
    <div className="text-center">
      <div className={`font-heading text-sm font-bold ${tone}`}>{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}
function safeDate(d) {
  try {
    return format(new Date(d), "PP p");
  } catch {
    return d;
  }
}
